import axios, { AxiosRequestConfig } from 'axios';
import { merge, round } from 'lodash';

const defaultLoadingDisplay = {
  display: () => {},
  hide: () => {}
};

class Request {
  #base_url;
  #config;
  #loadingDisplay;
  #successCallback;
  #errorCallback;

  constructor(
    base_url,
    config,
    loadingDisplay = defaultLoadingDisplay,
    successCallback = () => {},
    errorCallback = () => {}
  ) {
    this.#base_url = base_url;
    this.#config = config;
    this.#loadingDisplay = loadingDisplay;
    this.#successCallback = successCallback;
    this.#errorCallback = errorCallback;
  }

  /**
   * @private
   * @param {String} url
   * @returns {String}
   */
  addBaseUrl(url) {
    return this.#base_url + url;
  }

  /**
   * @private
   */
  successFormatter(response) {
    return {
      success: true,
      data: response.data
    };
  }

  /**
   * @private
   */
  handleError(error) {
    const formattedError = this.errorFormatter(error);
    this.#errorCallback(formattedError);

    return formattedError;
  }

  /**
   * @private
   */
  errorFormatter(error) {
    return {
      success: false,
      isCancelled: axios.isCancel(error),
      error: error.response,
      errorMessage:
        error?.response?.data?.message ||
        error?.response?.data?.description ||
        error?.response?.data?.msg ||
        error?.response?.data ||
        'Something went wrong'
    };
  }

  /**
   *
   * @param {Array<AxiosRequestConfig & {imaginary: boolean}} requests
   * @param {cancelCallBack} cancelCallback
   * @param {Function} progressCallback
   * @returns {Promise<requestResponse[]>}
   */
  async multiFetch(
    requests = [],
    cancelCallback = () => {},
    progressCallback = () => {}
  ) {
    let finishedRequests = 0;
    const totalRequests = requests.length;

    this.#loadingDisplay.display();

    const promises = requests.map(request =>
      request?.imaginary || false
        ? (async () => null)()
        : this.fetch2(request, cancelCallback)
    );

    for (const promise of promises) {
      promise.then(() => {
        progressCallback(round((++finishedRequests * 100) / totalRequests));
      });
    }

    const results = await Promise.all(promises);

    this.#loadingDisplay.hide();
    return results;
  }

  async multiFetch2(
    requests = [],
    cancelCallback = () => {},
    progressCallback = () => {}
  ) {
    let finishedRequests = 0;
    const totalRequests = requests.length;

    this.#loadingDisplay.display();

    const results = [];

    for (const request of requests) {
      const pages = request?.data?.initial_pages || 1;
      const pageRequests = [];

      // create all page requests
      for (let page = 1; page <= pages; page++) {
        pageRequests.push({
          ...request,
          data: {
            ...request.data,
            page
          }
        });
      }

      const pageResults = [];
      let pagingCompatible = true;
      let pagesLoaded = 0;

      for (let i = 0; i < pageRequests.length; i += 5) {
        const batch = pageRequests.slice(i, i + 5);

        const batchResults = await Promise.all(
          batch.map(req =>
            req?.imaginary
              ? (async () => null)()
              : this.fetch2(req, cancelCallback)
          )
        );

        for (const res of batchResults) {
          if (!res) continue;
          pagesLoaded++;

          // stop condition: if any page has no data
          if (!res?.data?.data?.length) {
            pagingCompatible = false;
            if (!pageResults.length) {
              results.push({
                ...res,
                data: { ...res.data, data: [] },
                pagingCompatible: false,
                pages_loaded: pagesLoaded,
                isLoading: false
              });
            }
            // bail out of both loops
            i = pageRequests.length;
            break;
          }

          pageResults.push(res);
        }
      }
      if (pageResults.length > 0) {
        // merge responses properly
        const compiledResult = pageResults.reduce((acc, res, idx) => {
          if (!res) return acc;
          if (res?.success === false) return res;

          if (idx === 0) {
            acc = {
              ...res,
              data: {
                ...res.data,
                data: [...res.data.data]
              }
            };
          } else {
            acc.data.data.push(...res.data.data);
          }
          return acc;
        }, null);

        if (compiledResult) {
          compiledResult.pagingCompatible = pagingCompatible;
          compiledResult.pages_loaded = pagesLoaded;
          results.push(compiledResult);
        }
      }
      finishedRequests++;
      progressCallback(Math.round((finishedRequests * 100) / totalRequests));
    }

    this.#loadingDisplay.hide();
    return results;
  }

  /**
   * @private
   */
  async fetch2(
    { method = 'GET', url = '', data = {}, ...props },
    cancelCallback = () => {}
  ) {
    const result = await axios({
      method,
      url: this.addBaseUrl(url),
      data,
      ...this.#config,
      ...props,
      cancelToken: new axios.CancelToken(cancelCallback)
    })
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });

    return result;
  }

  /**
   *
   * @param {AxiosRequestConfig} param0
   * @param {cancelCallBack} cancelCallback
   * @returns
   */
  async fetch(
    { method = 'GET', url = '', data = {}, ...props },
    cancelCallback = () => {}
  ) {
    this.#loadingDisplay.display();
    const result = await axios({
      method,
      url: this.addBaseUrl(url),
      data,
      ...this.#config,
      ...props,
      cancelToken: new axios.CancelToken(cancelCallback)
    })
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });
    this.#loadingDisplay.hide();
    return result;
  }

  /**
   * @private
   * @param {String} url
   * @param {AxiosRequestConfig} config
   */
  async axiosGet(url, config) {
    this.#loadingDisplay.display();
    const result = await axios
      .get(url, config)
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });
    this.#loadingDisplay.hide();
    return result;
  }

  /**
   * @private
   * @param {String} url
   * @param {{}} data
   * @param {AxiosRequestConfig} config
   */
  async axiosPost(url, data, config) {
    this.#loadingDisplay.display();
    const result = await axios
      .post(url, data, config)
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });
    this.#loadingDisplay.hide();

    return result;
  }

  /**
   * @private
   * @param {String} url
   * @param {{}} data
   * @param {AxiosRequestConfig} config
   */
  async axiosPut(url, data, config) {
    this.#loadingDisplay.display();
    const result = await axios
      .put(url, data, config)
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });
    this.#loadingDisplay.hide();

    return result;
  }

  /**
   * @private
   * @param {String} url
   * @param {{}} data
   * @param {AxiosRequestConfig} config
   */
  async axiosDelete(url, data, config) {
    this.#loadingDisplay.display();
    const result = await axios
      .delete(url, {
        ...config,
        data
      })
      .then(response => {
        return this.successFormatter(response);
      })
      .catch(error => {
        return this.handleError(error);
      });
    this.#loadingDisplay.hide();

    return result;
  }

  /**
   * @private
   * @param {AxiosRequestConfig} config
   * @param {cancelCallBack} cancelCallBack
   */
  mergeConfig(config, cancelCallBack) {
    return merge({}, this.#config, config, {
      cancelToken: new axios.CancelToken(cancelCallBack)
    });
  }

  /**
   *
   * @param {String} url
   * @param {AxiosRequestConfig} config
   * @param {cancelCallBack} cancelCallback
   * @returns {Promise<requestResponse>}
   */
  get(url, config = {}, cancelCallback = () => {}) {
    const urlWithBase = this.addBaseUrl(url);
    return this.axiosGet(urlWithBase, this.mergeConfig(config, cancelCallback));
  }

  /**
   *
   * @param {String} url
   * @param {Object} data
   * @param {AxiosRequestConfig} config
   * @param {cancelCallBack} cancelCallback
   * @returns {Promise<requestResponse>}
   */
  post(url, data = {}, config = {}, cancelCallback = () => {}) {
    const urlWithBase = this.addBaseUrl(url);
    return this.axiosPost(
      urlWithBase,
      data,
      this.mergeConfig(config, cancelCallback)
    );
  }

  /**
   *
   * @param {String} url
   * @param {Object} data
   * @param {AxiosRequestConfig} config
   * @param {cancelCallBack} cancelCallback
   * @returns {Promise<requestResponse>}
   */
  put(url, data = {}, config = {}, cancelCallback = () => {}) {
    const urlWithBase = this.addBaseUrl(url);
    return this.axiosPut(
      urlWithBase,
      data,
      this.mergeConfig(config, cancelCallback)
    );
  }

  /**
   *
   * @param {String} url
   * @param {Object} data
   * @param {AxiosRequestConfig} config
   * @param {cancelCallBack} cancelCallback
   * @returns {Promise<requestResponse>}
   */
  delete(url, data = {}, config = {}, cancelCallback = () => {}) {
    const urlWithBase = this.addBaseUrl(url);
    return this.axiosDelete(
      urlWithBase,
      data,
      this.mergeConfig(config, cancelCallback)
    );
  }

  overrideLoadingDisplay(loadingDisplay = defaultLoadingDisplay) {
    return new Request(this.#base_url, this.#config, loadingDisplay);
  }

  overrideWholeConfig(newConfig = {}) {
    return new Request(this.#base_url, newConfig, this.#loadingDisplay);
  }

  removeBaseUrl() {
    return new Request('', this.#config, this.#loadingDisplay);
  }
}

export default Request;
